/**
 * SitePick — `/sites/` カタログのファセット（progressive enhancement）。
 *
 * 基本はサーバ側 GET フォーム（JS 無しでも `?industry=…` で結果が返り、
 * ドロワーはインライン表示される）。JS がある時は:
 *   - `.p-archive` に `.is-catalog-enhanced` を付け、フィルターパネルをドロワー化
 *     （PC = 右オフキャンバス / SP = ボトムシート）。「絞り込み」ボタンで開閉。
 *     `aria-expanded` / Escape / バックドロップ / フォーカストラップ / スクロールロック。
 *   - リロードせずに結果（グリッド / 件数 / チップ / ページャ / 絞り込みバッジ）を差し替え。
 * 失敗時はフル遷移にフォールバック。SPA 化はしない。
 *
 * イベントは `.p-archive` に委譲する（差し替えでリスナが死なないように）。
 *
 * 計測: 結果が差し替わったときに `filter_sites`（件数つき）を 1 回だけ送る。
 * 件数 0 が「探したのに無かった」シグナルで、商品企画の需要検知に使う
 * （growth/ssot/measurement.yaml）。
 */

import { filterSites } from "./analytics.js";

const RESULT_NODES = [".p-archive__grid", ".p-archive__empty", ".p-pagination"];

/** URL の絞り込みパラメータ（`sitepick_facet_request()` と同じキー）。 */
const FACET_PARAMS = [
  "industry",
  "purpose",
  "impression",
  "palette",
  "feature",
  "typeface",
  "composition",
  "price_min",
  "price_max",
  "page_count_min",
  "page_count_max",
  "sort",
];
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * @param {ParentNode} root
 */
export function initFacet(root) {
  const main = root.querySelector(".p-archive");
  if (!main) {
    return;
  }

  const getForm = () => main.querySelector(".js-sp-facet");
  const getBar = () => main.querySelector(".p-facet-bar");
  const getPanel = () => main.querySelector(".p-facet-bar__panel");

  const enhanced = typeof window.fetch === "function" && typeof window.DOMParser === "function";
  if (enhanced) {
    main.classList.add("is-catalog-enhanced");
  }

  // --- 絞り込みドロワーの開閉 ---
  let lastToggle = null;

  function onTrapKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      closeSheet();
      return;
    }
    if (event.key !== "Tab") {
      return;
    }
    const panel = getPanel();
    if (!panel) {
      return;
    }
    const items = Array.from(panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
    if (items.length === 0) {
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openSheet(toggle) {
    const bar = getBar();
    if (!bar) {
      return;
    }
    lastToggle = toggle || main.querySelector(".js-sp-facet-toggle");
    bar.classList.add("is-facet-open");
    if (lastToggle) {
      lastToggle.setAttribute("aria-expanded", "true");
    }
    const backdrop = bar.querySelector(".p-facet-bar__backdrop");
    if (backdrop) {
      backdrop.hidden = false;
    }
    document.documentElement.classList.add("has-facet-sheet");
    document.body.classList.add("has-facet-sheet");
    const panel = getPanel();
    const focusTarget = (panel && panel.querySelector(".js-sp-facet-close")) || panel;
    if (focusTarget instanceof HTMLElement) {
      focusTarget.focus({ preventScroll: true });
    }
    document.addEventListener("keydown", onTrapKeydown);
  }

  function closeSheet() {
    const bar = getBar();
    if (!bar || !bar.classList.contains("is-facet-open")) {
      return;
    }
    bar.classList.remove("is-facet-open");
    const toggle = main.querySelector(".js-sp-facet-toggle");
    if (toggle) {
      toggle.setAttribute("aria-expanded", "false");
    }
    const backdrop = bar.querySelector(".p-facet-bar__backdrop");
    if (backdrop) {
      backdrop.hidden = true;
    }
    document.documentElement.classList.remove("has-facet-sheet");
    document.body.classList.remove("has-facet-sheet");
    document.removeEventListener("keydown", onTrapKeydown);
    if (lastToggle instanceof HTMLElement) {
      lastToggle.focus({ preventScroll: true });
    }
    lastToggle = null;
  }

  main.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) {
      return;
    }
    if (target.closest(".js-sp-facet-close")) {
      closeSheet();
      return;
    }
    const toggle = target.closest(".js-sp-facet-toggle");
    if (toggle) {
      const bar = getBar();
      if (bar && bar.classList.contains("is-facet-open")) {
        closeSheet();
      } else {
        openSheet(toggle);
      }
    }
  });

  /** ツールバーの「絞り込み N」バッジと件数を最新の DOM に合わせる。 */
  function syncBadges() {
    const active = main.querySelector("[data-sp-facet-active]");
    if (active) {
      const n = parseInt(active.textContent.trim() || "0", 10) || 0;
      active.classList.toggle("is-empty", n === 0);
    }
  }
  syncBadges();

  if (!enhanced) {
    return;
  }

  let controller = null;
  let debounce = 0;

  /** フォームの現在値から `/sites/?…` のきれいな URL を組む（tax は CSV）。 */
  function buildUrl() {
    const form = getForm();
    if (!form) {
      return window.location.href;
    }
    const fd = new FormData(form);
    const multi = Object.create(null);
    const params = new URLSearchParams();
    for (const [rawKey, rawVal] of fd.entries()) {
      const value = String(rawVal).trim();
      if (!value) {
        continue;
      }
      if (rawKey.endsWith("[]")) {
        const key = rawKey.slice(0, -2);
        (multi[key] || (multi[key] = [])).push(value);
      } else if (rawKey === "sort") {
        if (value !== "recommended") {
          params.set("sort", value);
        }
      } else {
        params.set(rawKey, value);
      }
    }
    for (const key in multi) {
      params.set(key, multi[key].join(","));
    }
    const base = form.getAttribute("action") || window.location.pathname;
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }

  /**
   * 取得した HTML の要素を live DOM へ持ち込む。
   *
   * DOMParser の文書は scripting 無効なので `<noscript>` の中身が実要素（`<style>` 等）として
   * パースされる。それをそのまま import すると JS 有効なのに noscript 用のスタイルが効き、
   * ドロワーがインライン展開したままスクロールロックがかかる。JS が動いている以上
   * `<noscript>` は不要なので、import 前に必ず取り除く。
   *
   * @param {Element} el
   * @returns {Element}
   */
  function importFragment(el) {
    const clone = document.importNode(el, true);
    clone.querySelectorAll("noscript").forEach((node) => node.remove());
    return clone;
  }

  /**
   * 一覧の先頭までスクロールする。ページャはリロードしないので、押しても
   * 画面が動かず「切り替わったのか分からない」状態になるため。
   * 固定ヘッダーの高さぶんを引いて、件数の行が隠れないようにする。
   */
  function scrollToListTop() {
    const anchor = main.querySelector(".p-archive__count") || main.querySelector(".p-archive__grid");
    if (!anchor) {
      return;
    }
    const header = document.querySelector(".l-header");
    const offset = (header ? header.getBoundingClientRect().height : 0) + 12;
    const top = window.scrollY + anchor.getBoundingClientRect().top - offset;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: Math.max(top, 0), behavior: reduce ? "auto" : "smooth" });
  }

  /**
   * 件数表示（「52件のサイト」）から整数を取り出す。3 桁区切り・全角数字にも耐える。
   *
   * @param {Element|null} el
   * @returns {number|null} 取れなければ null（推測で 0 を入れない）
   */
  function readCount(el) {
    if (!el) {
      return null;
    }
    const normalized = el.textContent.replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0));
    const digits = normalized.replace(/,/g, "").match(/\d+/);
    return digits ? Number.parseInt(digits[0], 10) : null;
  }

  /**
   * 絞り込み結果を 1 回だけ計測する（`filter_sites`）。
   * `result_count === 0` がゼロ件シグナル。件数を読めなかったときは送らない
   * （0 件と読めなかったことを混同しない）。
   *
   * @param {string} url 差し替え後の URL
   * @param {number|null} count
   */
  function trackFilter(url, count) {
    if (count === null) {
      return;
    }
    let params;
    try {
      params = new URL(url, window.location.origin).searchParams;
    } catch {
      return;
    }
    const payload = { result_count: count };
    FACET_PARAMS.forEach((key) => {
      const value = params.get(key);
      if (value) {
        payload[`sp_${key}`] = value;
      }
    });
    filterSites(payload);
  }

  /** 新しい HTML から結果 / 件数 / チップ / 絞り込みバッジ / フォーム状態を反映する。 */
  function swapResults(nextMain, syncForm) {
    const count = main.querySelector(".p-archive__count");
    const nextCount = nextMain.querySelector(".p-archive__count");
    if (count && nextCount) {
      count.textContent = nextCount.textContent.trim();
    }

    // 適用中チップ（フォーム外・件数の下）。無くなったら消す。
    const curChips = main.querySelector(".p-catalog-chips");
    const nextChips = nextMain.querySelector(".p-catalog-chips");
    if (curChips && nextChips) {
      curChips.replaceWith(importFragment(nextChips));
    } else if (curChips && !nextChips) {
      curChips.remove();
    } else if (!curChips && nextChips) {
      const anchor = main.querySelector(".p-facet-bar");
      if (anchor) {
        anchor.after(importFragment(nextChips));
      }
    }

    RESULT_NODES.forEach((sel) => {
      const el = main.querySelector(sel);
      if (el) {
        el.remove();
      }
    });

    // 結果は「チップ → ツールバー（フォーム）→ 件数」の順で最初に見つかった要素の
    // 直後に挿入する（件数がフォームの上にあるので count.after だとバーが下に回ってしまう）。
    let after =
      main.querySelector(".p-catalog-chips") ||
      main.querySelector(".p-facet-bar") ||
      count;
    RESULT_NODES.forEach((sel) => {
      const el = nextMain.querySelector(sel);
      if (el && after) {
        const clone = importFragment(el);
        after.after(clone);
        after = clone;
      }
    });

    if (syncForm) {
      const curBar = main.querySelector(".p-facet-bar");
      const nextBar = nextMain.querySelector(".p-facet-bar");
      if (curBar && nextBar) {
        document.documentElement.classList.remove("has-facet-sheet");
        document.body.classList.remove("has-facet-sheet");
        lastToggle = null;
        curBar.replaceWith(importFragment(nextBar));
      }
    } else {
      // ライブ適用: バーは差し替えないが、絞り込みバッジだけ次の値へ。
      const curActive = main.querySelector("[data-sp-facet-active]");
      const nextActive = nextMain.querySelector("[data-sp-facet-active]");
      if (curActive && nextActive) {
        curActive.textContent = nextActive.textContent.trim();
      }
    }
    syncBadges();
  }

  /**
   * @param {string} url
   * @param {{push?: boolean, syncForm?: boolean, scrollTop?: boolean}} [opts]
   */
  async function load(url, opts = {}) {
    const { push = true, syncForm = false, scrollTop = false } = opts;
    if (controller) {
      controller.abort();
    }
    controller = new AbortController();
    main.classList.add("is-facet-loading");

    let html;
    try {
      const res = await fetch(url, {
        signal: controller.signal,
        credentials: "same-origin",
        headers: { "X-Requested-With": "sitepick-facet" },
      });
      if (!res.ok) {
        throw new Error(String(res.status));
      }
      html = await res.text();
    } catch (err) {
      if (err && err.name === "AbortError") {
        return;
      }
      window.location.assign(url);
      return;
    }

    const nextMain = new DOMParser().parseFromString(html, "text/html").querySelector(".p-archive");
    main.classList.remove("is-facet-loading");
    if (!nextMain) {
      window.location.assign(url);
      return;
    }

    swapResults(nextMain, syncForm);
    if (scrollTop) {
      scrollToListTop();
    }
    if (push) {
      window.history.pushState({ spFacet: true }, "", url);
    }
    // 差し替えが確定してから 1 回だけ計測する（fetch 失敗やアボート時は送らない）。
    trackFilter(url, readCount(main.querySelector(".p-archive__count")));
    document.dispatchEvent(new CustomEvent("sitepick:content:updated"));
  }

  // --- フォーム操作（`.p-archive` に委譲） ---
  main.addEventListener("submit", (event) => {
    if (!(event.target instanceof Element) || !event.target.matches(".js-sp-facet")) {
      return;
    }
    event.preventDefault();
    window.clearTimeout(debounce);
    closeSheet();
    load(buildUrl(), { push: true, syncForm: true });
  });

  // ライブ適用は `syncForm: false`（フォーム / ドロワーの開閉状態を保持したまま
  // グリッド・件数・チップ・絞り込みバッジだけ差し替える）。
  main.addEventListener("change", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.closest(".js-sp-facet")) {
      return;
    }
    if (target.matches("select[name='sort']")) {
      window.clearTimeout(debounce);
      load(buildUrl(), { push: true, syncForm: false });
    } else if (target.matches("input[type='checkbox'], input[type='number']")) {
      window.clearTimeout(debounce);
      debounce = window.setTimeout(() => load(buildUrl(), { push: true, syncForm: false }), 450);
    }
  });

  // --- ページャ / 条件クリア / チップ のリンクを横取り ---
  main.addEventListener("click", (event) => {
    const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
    if (!link) {
      return;
    }
    if (
      !link.closest(".p-pagination") &&
      !link.closest(".p-facet-bar__actions") &&
      !link.closest(".p-catalog-chips") &&
      !link.closest(".p-archive__empty")
    ) {
      return;
    }
    event.preventDefault();
    window.clearTimeout(debounce);
    closeSheet();
    load(link.getAttribute("href"), {
      push: true,
      syncForm: true,
      scrollTop: Boolean(link.closest(".p-pagination")),
    });
  });

  window.addEventListener("popstate", () => {
    load(window.location.href, { push: false, syncForm: true });
  });
}
